import {
  APPROVAL_DECISION_LABELS,
  APPROVAL_MODE_LABELS,
  APPROVAL_STATE_LABELS,
  ApprovalDecisionValue,
  ApprovalState,
  type TicketApproval,
} from '@gb-ticket/shared-types';
import { Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDecideApproval } from '@/products/helpdesk/features/approvals/use-approvals';
import {
  TicketSectionCard,
  type TicketSectionTone,
} from '@/products/helpdesk/features/tickets/ticket-section-card';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

function approvalTone(status: TicketApproval['status']): TicketSectionTone {
  if (status === ApprovalState.APPROVED) return 'emerald';
  if (status === ApprovalState.REJECTED || status === ApprovalState.CANCELLED) return 'red';
  return 'amber';
}

export function TicketApprovalPanel({
  ticketRef,
  approval,
}: {
  ticketRef: string;
  approval: TicketApproval;
}) {
  const decide = useDecideApproval(ticketRef);
  const [comment, setComment] = useState('');
  const tone = approvalTone(approval.status);
  const keepOpen = approval.canDecide || approval.status === ApprovalState.PENDING;

  async function onDecide(
    decision: typeof ApprovalDecisionValue.APPROVED | typeof ApprovalDecisionValue.REJECTED,
  ) {
    try {
      await decide.mutateAsync({
        requestId: approval.requestId,
        body: { decision, comment: comment.trim() || undefined },
      });
      setComment('');
      toast.success(
        decision === ApprovalDecisionValue.APPROVED ? 'Freigabe erteilt.' : 'Freigabe abgelehnt.',
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Entscheidung konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <TicketSectionCard
      tone={tone}
      icon={ShieldCheckIcon}
      collapsible
      defaultOpen={keepOpen}
      title="Freigabe"
      description={`${approval.workflowName} · ${APPROVAL_STATE_LABELS[approval.status]}`}
    >
      <ol className="space-y-3">
        {approval.steps.map((step) => (
          <li
            key={step.id}
            className={cn(
              'rounded-md border p-3 text-sm',
              step.isCurrent && approval.status === ApprovalState.PENDING
                ? 'border-amber-300 bg-amber-50/80'
                : 'bg-background/60',
            )}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium">
                {step.position + 1}. {step.name}
                {step.isCurrent ? ' (aktuell)' : ''}
              </p>
              <p className="text-muted-foreground">
                {APPROVAL_MODE_LABELS[step.mode]} · {APPROVAL_STATE_LABELS[step.status]}
              </p>
            </div>
            {step.roleName && <p className="mt-1 text-muted-foreground">Rolle: {step.roleName}</p>}
            {step.pendingApprovers.length > 0 && (
              <p className="mt-1">
                Ausstehend: {step.pendingApprovers.map((person) => person.displayName).join(', ')}
              </p>
            )}
            {step.decisions.map((item) => (
              <p key={item.id} className="mt-1">
                {item.approver.displayName}: {APPROVAL_DECISION_LABELS[item.decision]}
                {item.comment ? ` – ${item.comment}` : ''}
              </p>
            ))}
          </li>
        ))}
      </ol>

      {approval.status === ApprovalState.PENDING && approval.canDecide && (
        <div className="mt-4 space-y-3 border-t pt-3">
          <div className="space-y-1.5">
            <Label htmlFor="approval-comment">Kommentar (optional)</Label>
            <Input
              id="approval-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Begründung"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={decide.isPending}
              onClick={() => void onDecide(ApprovalDecisionValue.APPROVED)}
            >
              {decide.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Freigeben
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={decide.isPending}
              onClick={() => void onDecide(ApprovalDecisionValue.REJECTED)}
            >
              Ablehnen
            </Button>
          </div>
        </div>
      )}

      {approval.status === ApprovalState.PENDING && !approval.canDecide && (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900/80">
          Das Ticket ist bis zur Freigabe pausiert. Statuswechsel sind gesperrt.
        </p>
      )}
    </TicketSectionCard>
  );
}
